import React from 'react';
import { animated, config, useSpring } from 'react-spring';
import useMeasure from 'react-use-measure';
import styled from 'styled-components';
import {
  CheckCircleFilledIcon,
  EditIcon,
  MinusCircleFilledIcon,
} from '../../assets';
import { BodyText, Heading, UIText } from '../typography';
import { breakpoint, theme } from '../../theme';
import { useBreakpointMap } from '../../utils';

export type ProgressBarStepType = {
  stepNumber: number;
  heading: string;
  subHeading?: string;
  isComplete: boolean;
  isDisabled?: boolean;
  isLocked?: boolean;
  stepContent: React.ReactNode;
  stepCompletedContent: React.ReactNode;
  nextButtonText?: string;
  isEditable: boolean;
};

export type ProgressBarBasicProps = {
  currentStep: number;
  setStep: (step: number) => void;
};

export type ProgressBarStepProps = ProgressBarStepType & ProgressBarBasicProps;

const StepContainer = styled.div`
  position: relative;
  z-index: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${theme.spacings[4].px};
  border-bottom: 1px solid ${theme.colors.neutrals.solidGrays[7]};

  :last-child {
    border-bottom: 0;
  }

  :last-child > div:last-child {
    border-bottom: 0;
  }

  ${breakpoint('lg')`
    flex-direction: row;
    border-bottom: 0;
    gap: 23px;
     div > :not(:last-child)&::before {
    display: inline-block;
    content: '';
    position: absolute;
    z-index: -1;
    top: 35px;
    left: 20px;
    width: 1px;
    height: 100%;
    background-color: ${theme.colors.neutrals.solidGrays[6]};
  }  
  `}
`;

const Content = styled.div<{
  isDisabled: boolean | undefined;
  isComplete: boolean | undefined;
  isLocked?: boolean;
}>`
  display: flex;
  flex-direction: column;

  opacity: ${({ isDisabled, isComplete }) =>
    isDisabled && !isComplete ? 0.5 : 1};
  pointer-events: ${({ isDisabled, isComplete }) =>
    isDisabled && !isComplete ? 'none' : 'auto'};
  width: 100%;

  ${breakpoint('lg')`
     gap: 16px;
      border-bottom: 1px solid ${theme.colors.neutrals.solidGrays[7]};
    `}
`;

const HeadingContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`;

const LinkContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacings[2].px};
  margin-left: ${theme.spacings[4].px};
`;

const StyledLink = styled(UIText).attrs({ size: 'md' })`
  cursor: pointer;
  color: ${theme.colors.primary.graysBlue};
  font-weight: bold;
`;

const StyledEditIcon = styled(EditIcon)`
  width: 20px;
  height: 20px;
  fill: ${theme.colors.primary.graysBlue};
`;

const StyledHeading = styled(Heading)<{
  isDisabled?: boolean;
  isComplete?: boolean;
  isLocked?: boolean;
}>`
  ${theme.typography.headings[4]['xs-md']}
  color: ${({ isLocked, theme }) =>
    isLocked ? theme.colors.primary.graysRed : theme.colors.primary.graysGray};
  opacity: ${({ isDisabled, isComplete }) =>
    isDisabled && !isComplete ? 0.5 : 1};
  pointer-events: ${({ isDisabled, isComplete }) =>
    isDisabled && !isComplete ? 'none' : 'auto'};
  ${breakpoint('lg')`
    ${theme.typography.headings[4]['md-xl']};    
  `}
`;

const StyledSubHeading = styled(BodyText).attrs({ size: 'md' })<{
  isLocked?: boolean;
}>`
  color: ${({ isLocked, theme }) =>
    isLocked ? theme.colors.primary.graysRed : theme.colors.primary.graysGray};
`;

const StyledCheckCircleFilledIcon = styled(CheckCircleFilledIcon)`
  margin-top: ${theme.spacings[1].px};
  ${breakpoint('lg')`
       margin-top: 0;
    `}
`;
const StyledMinusCircleFilledIcon = styled(MinusCircleFilledIcon)`
  margin-top: ${theme.spacings[1].px};
  ${breakpoint('lg')`
       margin-top: 0;
    `}
`;
const ChildrenContainer = styled.div<{
  isLocked?: boolean;
}>`
  width: 100%;
  color: ${({ isLocked, theme }) => isLocked && theme.colors.primary.graysRed};
`;

const HeadingIconContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
`;

const StepNumber = styled.div<{ isDisabled: boolean | undefined }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 24px;
  border-radius: 50px;
  margin-top: ${theme.spacings[1].px};
  ${theme.typography.headings[6]['md-xl']}
  background-color:${theme.colors.neutrals.solidGrays[7]};
  ${breakpoint('lg')`
    margin-top: 0;
      width: 40px;
  height: 40px;
   ${theme.typography.headings[4]['md-xl']}
    `}
  color: ${({ isDisabled }) =>
    isDisabled
      ? theme.colors.neutrals.text.tertiary
      : theme.colors.primary.graysGray};
`;

export const ProgressBarStep: React.FC<ProgressBarStepProps> = ({
  stepNumber,
  heading,
  subHeading,
  isComplete,
  isDisabled,
  isLocked = true,
  stepContent,
  stepCompletedContent,
  isEditable,
  currentStep,
  setStep,
}) => {
  const viewportSize = useBreakpointMap({
    xs: 'Responsive',
    sm: 'tablet',
    md: 'Desktop',
  });

  const isMobile = viewportSize !== undefined && viewportSize !== 'Desktop';
  const isOpen = currentStep === stepNumber;

  const editStep = () => {
    setStep(stepNumber);
  };

  const enableEditButton =
    isMobile &&
    isComplete &&
    isEditable &&
    !isDisabled &&
    currentStep !== stepNumber;
  const enableEditButtonDesktop =
    isComplete && isEditable && !isDisabled && currentStep !== stepNumber;
  const completedContent = isComplete && currentStep !== stepNumber;

  return (
    <StepContainer>
      <ProgressBarHeader
        isComplete={isComplete}
        isLocked={isLocked}
        isMobile={isMobile}
        heading={heading}
        stepNumber={stepNumber}
        editStep={editStep}
        isOpen={isOpen}
        enableEditButton={enableEditButton}
      />

      <ProgressBarContent
        isComplete={isComplete}
        isLocked={isLocked}
        isMobile={isMobile}
        heading={heading}
        subHeading={subHeading}
        stepNumber={stepNumber}
        stepCompletedContent={stepCompletedContent}
        stepContent={stepContent}
        editStep={editStep}
        isOpen={isOpen}
        completedContent={completedContent}
        enableEditButtonDesktop={enableEditButtonDesktop}
      />
    </StepContainer>
  );
};

const ProgressBarHeader: React.FC<
  Pick<
    ProgressBarStepProps,
    'heading' | 'stepNumber' | 'isComplete' | 'isLocked'
  > & {
    isMobile: boolean;
    isOpen: boolean;
    enableEditButton: boolean;
    editStep: () => void;
  }
> = ({
  heading,
  stepNumber,
  isComplete,
  isLocked,
  isMobile,
  isOpen,
  editStep,
  enableEditButton,
}) => {
  const viewBox = isMobile ? '2 2 13 13' : '2 2 12 12';
  const width = isMobile ? 24 : 40;
  const fill = isComplete
    ? theme.colors.system.systemGreen
    : theme.colors.system.systemRed;

  const Icon = () =>
    isComplete ? (
      <StyledCheckCircleFilledIcon
        viewBox={viewBox}
        width={width}
        fill={fill}
      />
    ) : isLocked ? (
      <StyledMinusCircleFilledIcon
        viewBox={viewBox}
        width={width}
        fill={fill}
      />
    ) : (
      <StepNumber isDisabled={!isOpen}> {stepNumber}</StepNumber>
    );
  const Header = () => {
    if (isMobile) {
      return (
        <StyledHeading
          isDisabled={!isOpen}
          isComplete={isComplete}
          isLocked={isLocked}
        >
          {heading}
        </StyledHeading>
      );
    }
    return null;
  };

  return (
    <HeadingIconContainer>
      <Icon />
      <Header />
      {enableEditButton && (
        <LinkContainer>
          <StyledEditIcon onClick={editStep} />
          <StyledLink onClick={editStep}>Edit</StyledLink>
        </LinkContainer>
      )}
    </HeadingIconContainer>
  );
};

const ProgressBarContent: React.FC<
  Pick<
    ProgressBarStepProps,
    | 'heading'
    | 'stepNumber'
    | 'isComplete'
    | 'isLocked'
    | 'subHeading'
    | 'stepContent'
    | 'stepCompletedContent'
  > & {
    isMobile: boolean;
    isOpen: boolean;
    enableEditButtonDesktop: boolean;
    completedContent: boolean;
    editStep: () => void;
  }
> = ({
  heading,
  stepCompletedContent,
  stepContent,
  isComplete,
  isLocked,
  isMobile,
  subHeading,
  isOpen,
  editStep,
  enableEditButtonDesktop,
  completedContent,
}) => {
  const lockedDesktop = isLocked && !isMobile;

  const Header = () => {
    if (isMobile) {
      return null;
    }
    if (lockedDesktop) {
      return (
        <>
          <HeadingContainer>
            <StyledHeading isLocked={isLocked}>{heading}</StyledHeading>
          </HeadingContainer>
          {subHeading && (
            <StyledSubHeading isLocked={isLocked}>
              {subHeading}
            </StyledSubHeading>
          )}
        </>
      );
    } else {
      return (
        <>
          <HeadingContainer>
            <StyledHeading>{heading}</StyledHeading>
            {enableEditButtonDesktop && (
              <LinkContainer>
                <StyledEditIcon onClick={editStep} />
                <StyledLink onClick={editStep}>Edit</StyledLink>
              </LinkContainer>
            )}
          </HeadingContainer>
          {subHeading && <StyledSubHeading>{subHeading}</StyledSubHeading>}
        </>
      );
    }
  };

  return (
    <>
      <Content isDisabled={!isOpen} isComplete={isComplete}>
        <Header />
        <ProgressContent
          isOpen={isOpen}
          completedContent={completedContent}
          isLocked={isLocked}
          stepContent={stepContent}
          stepCompletedContent={stepCompletedContent}
        />
      </Content>
    </>
  );
};

const ProgressContent: React.FC<any> = ({
  isOpen,
  completedContent,
  isLocked,
  stepContent,
  stepCompletedContent,
}) => {
  const [measureRef, { height }] = useMeasure();
  const styles = useSpring({
    config: config.molasses,
    from: {
      height: 0,
      overflow: 'hidden',
    },
    to: {
      height: isOpen ? height : 0,
      overflowY: 'hidden',
    },

    reverse: !isOpen ? true : false,
  });

  if (isLocked) {
    return <ChildrenContainer>{stepContent}</ChildrenContainer>;
  } else if (completedContent) {
    return <ChildrenContainer>{stepCompletedContent}</ChildrenContainer>;
  } else {
    return (
      <animated.div style={styles}>
        <ChildrenContainer ref={measureRef}>{stepContent}</ChildrenContainer>
      </animated.div>
    );
  }
};
